import "./EventAPIKeyEditView.scss";
import { debounce, flatten } from "lodash";
import ImagePreview from "@/components/Common/ImagePreview/ImagePreview.vue";
import TreeViewSelection from "@/components/Common/TreeViewSelection/TreeViewSelection.vue";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import FallbackPictureCopyrightEditView from "@/components/Common/FallbackPictureCopyrightEditView/FallbackPictureCopyrightEditView.vue";
import ColorPickerButton from "@/components/Common/ColorPickerButton/ColorPickerButton.vue";
import LogOverview from "@/components/Common/LogOverview/LogOverview.vue";
import VuePrism from "vue-prism";
import "prismjs/themes/prism-okaidia.min.css";
import {
  getNewTeaserHTML,
  getNewFakeTableHTML,
  getNewAccordionHTML,
  getNewListHTML,
  getNewDetailHTML,
  getDetailHTML,
  getListHTML,
  getTeaserHTML,
  getReportWidgetHTML
} from "@/helpers/eventHelper";
import { initGlobalCopyToClipboard, escapeHTML } from "@/helpers/commonHelper";
import InfoDialog from "@/components/Common/InfoDialog/InfoDialog.vue";
import { uploadFile } from "@/helpers/fileHelper";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

export default {
  name: "EventAPIKeyEditView",
  data: () => ({
    categoryGroupText: "",
    containCategoriesText: "",
    categoryText: "",
    featureText: "",
    featureGroupText: "",
    showNotepad: false,
    apiKeyPermissions: [
      { value: "edit", title: "Bearbeiten" },
      { value: "view", title: "Sehen" },
      { value: "none", title: "Keine" }
    ],
    sortDirection: [
      { value: "asc", title: "Aufsteigend" },
      { value: "desc", title: "Absteigend" }
    ],
    sortProperties: [
      { value: "moment", title: "Datum" },
      { value: "title_asc", title: "Titel" },
      { value: "location", title: "Ort" }
    ],
    regionality: [
      { title: "Alle", value: "", default: true },
      { title: "Regional", value: "communal" },
      { title: "Lokal", value: "local" },
      { title: "Überregional", value: "regional" },
      { title: "Intern", value: "internal" }
    ],
    selectableRegionalities: [
      { title: "Regional", value: "communal" },
      { title: "Lokal", value: "local" },
      { title: "Überregional", value: "regional" },
      { title: "Intern", value: "internal" }
    ],
    cityFilterItems: [
      {
        title: "Standort (übergeordnet)",
        value: "normalized_city",
        default: true
      },
      { title: "Standort (Ort)", value: "city" }
    ],
    notDefinedNormalizedCity: {
      name: "kein Standort",
      value: "not-defined"
    },
    accordionsOpenItems: [
      { value: "first", title: "Erstes geöffnet" },
      { value: "closed", title: "Alle Geschlossen" },
      { value: "all", title: "Alle geöffnet" }
    ],
    startDateValues: [
      { value: 0, title: "heute" },
      { value: 1, title: "morgen" },
      { value: 2, title: "in 2 Tagen" },
      { value: 3, title: "in 3 Tagen" },
      { value: 4, title: "in 4 Tagen" },
      { value: 5, title: "in 5 Tagen" },
      { value: 7, title: "in 1 Woche" }
    ],
    endDateValues: [
      { value: 0, title: "Tag" },
      { value: 7, title: "Woche" },
      { value: 30, title: "Monat" },
      { value: 365, title: "Jahr" }
    ],
    listStyles: [
      { value: "listMap", text: "Liste mit Karte" },
      { value: "gridMap", text: "Grid mit Karte" },
      { value: "splitGridMap", text: "Geteiltes Grid mit Karte" },
      { value: "list", text: "Liste ohne Karte" },
      { value: "grid", text: "Grid ohne Karte" },
      { value: "splitGrid", text: "Geteiltes Grid ohne Karte" },
      { value: "table", text: "Tabelle" },
      { value: "accordion", text: "Akkordion" }
    ],
    outputFormats: [
      { value: "compact", title: "Kompakt" },
      { value: "standard", title: "Standard" }
    ],
    overlayModes: [
      { value: "dark", title: "Dark-Mode" },
      { value: "light", title: "Light-Mode" },
      { value: "overlay", title: "Overlay-Mode" }
    ],
    sliderModes: [
      { value: "sliderModeStandard", title: "Standard" },
      { value: "sliderModeOverlay", title: "Overlay" }
    ],
    useHighlightFromItems: [
      { title: "Standard", value: "standard" },
      { title: "Eigen", value: "own" }
    ],
    cityFilterLimitationItems: [
      { title: "Immer alle Orte anzeigen", value: "doNotLimit" },
      { title: "Auf Filterung eingrenzen", value: "limit" }
    ],
    pictureArray: [],
    snackbarText: "",
    snackbarColor: "",
    snackbar: false,
    locationDisplayModes: [
      { title: "Standortname", value: "locationName" },
      { title: "Ort", value: "city" },
      { title: "Standortname und Ort", value: "locationNameAndCity" }
    ],
    showIframePreview: {
      list: false,
      teaser: false,
      accordion: false,
      faketable: false,
      report: false
    },
    refreshIframePreview: {
      list: 0,
      teaser: 0,
      accordion: 0,
      faketable: 0,
      report: 0
    }
  }),
  props: ["apiKey", "apiKeyValid", "channels"],
  async mounted() {
    await this.$store.dispatch("getAllUsers");
    const subPrincipals = [
      ...this.apiKey.selectedPrincipals,
      this.getSelectedPrincipalIds()
    ].filter(_ => !!_);
    await this.$store.dispatch("getOrganizer", {
      eventList: true,
      filter: {
        subPrincipals: subPrincipals
      }
    });
    this.initGlobalCopyToClipboard();
  },
  computed: {
    isValid: {
      get() {
        return this.apiKeyValid;
      },
      set(value) {
        this.$emit("setApiKeyValid", value);
      }
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    isReadOnly() {
      if (this.isAdmin) {
        return false;
      }
      if (this.apiKey.userPermission === "edit") {
        return false;
      }
      return true;
    },
    outputs() {
      const defaultValue = [
        { value: "", title: "Immer Allgemeine Beschreibung" }
      ];
      return defaultValue.concat(this.channels);
    },
    categoryGroups() {
      return this.$store.state.categoryGroupsForEvents;
    },
    users() {
      return this.$store.state.users.filter(
        user => user.principal_id === this.$store.state.selectedPrincipal._id
      );
    },
    featureGroups() {
      return this.$store.state.featureGroupsForEvents?.filter(
        fg => !!fg.features?.length
      );
    },
    categoryTypes() {
      return this.$store.state.categoryTypes;
    },
    categories() {
      return flatten(this.categoryGroups.map(cg => cg.categories));
    },
    features() {
      return flatten(this.featureGroups?.map(fg => fg.features));
    },
    locations() {
      return this.$store.state.locations;
    },
    cities() {
      const cities = this.locations
        .filter(location => !!location.address?.city)
        .map(location => ({
          name: location.address.city,
          value: location.address.city
        }));
      cities.sort((a, b) => a.value.localeCompare(b.value));
      return cities;
    },
    normalizedCities() {
      const normalizedCities = this.locations
        .filter(location => !!location.address?.normalized_city)
        .map(location => ({
          name: location.address.normalized_city,
          value: location.address.normalized_city
        }));
      normalizedCities.sort((a, b) => a.value.localeCompare(b.value));
      return [this.notDefinedNormalizedCity, ...normalizedCities];
    },
    availablePreSelectedCities() {
      const availablePreSelectedCities = [...this.normalizedCities];
      return availablePreSelectedCities.filter(
        cityObj => cityObj.value !== this.notDefinedNormalizedCity.value
      );
    },
    principals() {
      return this.$store.state.principals;
    },
    fallbackPicture: {
      get() {
        return this.apiKey.fallbackPicture;
      },
      set(newVal) {
        this.pictureArray = [];
        this.pictureArray.push(newVal);
        this.apiKey.fallbackPicture = newVal;
      }
    },
    // Update computed property only when 'refreshIframePreview' is increased in debounced function 'setCodePreview'
    // as to not reload the iframe for every single color change (e.g. by selecting a color via the color-picker canvas).
    iframeSrcListDebounced() {
      if (++this.refreshIframePreview.list) {
        return this.getIframeSrc("events/", this.apiKey.newWidget);
      }
    },
    iframeSrcTeaserDebounced() {
      if (++this.refreshIframePreview.teaser) {
        return this.getIframeSrc("teaser/", this.apiKey.newWidget);
      }
    },
    iframeSrcAccordionDebounced() {
      if (++this.refreshIframePreview.accordion) {
        return this.getIframeSrcAccordion();
      }
    },
    iframeSrcFaketableDebounced() {
      if (++this.refreshIframePreview.faketable) {
        return this.getIframeSrcFaketable();
      }
    },
    iframeSrcReportWidgetDebounced() {
      if (++this.refreshIframePreview.report) {
        return this.getIframeSrcReportWidget();
      }
    },
    logUrl() {
      return `${env.events_api_client_url}log/${this.apiKey._id}`;
    }
  },
  methods: {
    initGlobalCopyToClipboard,
    escapeHTML,
    getListHTML,
    getTeaserHTML,
    getDetailHTML,
    getNewTeaserHTML,
    getNewFakeTableHTML,
    getNewAccordionHTML,
    getNewListHTML,
    getNewDetailHTML,
    getReportWidgetHTML,
    getParamsForIframeSrc() {
      let c = "?";
      const paramsObj = {
        apiKey: this.apiKey.apiKey,
        ...this.apiKey.color
      };
      for (const key of Object.keys(paramsObj)) {
        if (key.startsWith("map")) {
          delete paramsObj[key];
        }
      }
      let params = "";
      for (const [key, value] of Object.entries(paramsObj)) {
        params += `${c}${key}=${encodeURIComponent(value)}`;
        c = "&";
      }
      return params;
    },
    getIframeSrc(listOrTeaser, newWidget) {
      if (!listOrTeaser.endsWith("/")) {
        listOrTeaser += "/";
      }
      const serviceUrl = newWidget
        ? env.events_widget_api_url_v2
        : env.events_widget_api_url;

      return `${serviceUrl}${listOrTeaser}${this.getParamsForIframeSrc()}`;
    },
    getIframeSrcAccordion() {
      return `${
        env.events_widget_api_url_v2
      }accordion/${this.getParamsForIframeSrc()}`;
    },
    getIframeSrcFaketable() {
      return `${
        env.events_widget_api_url_v2
      }faketable/${this.getParamsForIframeSrc()}`;
    },
    getIframeSrcReportWidget() {
      return `${
        env.events_report_widget_api_url
      }${this.getParamsForIframeSrc()}`;
    },
    getIframeHTML(listOrTeaser, newWidget) {
      return `<iframe src="${this.getIframeSrc(
        listOrTeaser,
        newWidget
      )}"></iframe>`;
    },

    getIframeAccordionHTML() {
      return `<iframe src="${this.getIframeSrcAccordion()}"></iframe>`;
    },

    getIframeFaketableHTML() {
      return `<iframe src="${this.getIframeSrcFaketable()}"></iframe>`;
    },

    getIframeHTMLReportWidget() {
      return `<iframe src="${this.getIframeSrcReportWidget()}"></iframe>`;
    },
    getPreviewOuterHTML(innerHTML) {
      const randomId = (Math.random() + 1).toString(36).substring(7);
      const copyButtonHTML = `<p class="copy-code-preview"
      onClick="copyCodePreviewToClipboard('${randomId}');"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12V1Z"/></svg>
        <span>Kopieren</span>
      </p>`;
      return `${copyButtonHTML}<pre class="language-markup" id="${randomId}"><code>${this.escapeHTML(
        innerHTML
      )}</code></pre>`;
    },
    setColorAndCodePreview(colorKey, colorValue) {
      if (this.isReadOnly) {
        return;
      }
      if (colorKey && colorValue) {
        this.$set(this.apiKey.color, colorKey, colorValue);
      }
      this.$nextTick(() => {
        this.$forceUpdate();
        this.setCodePreview();
      });
    },
    setCodePreview: debounce(function() {
      const codePreviewContainers = document.querySelectorAll(
        ".widget-code-preview"
      );
      if (!codePreviewContainers?.length) {
        return;
      }
      Array.from(codePreviewContainers).forEach(codePreviewContainer => {
        const codePreviewElement = codePreviewContainer.querySelector("pre");

        const codePreviewFor =
          codePreviewContainer.attributes["data-code-preview-for"].value;
        const isNewWidget = this.apiKey.newWidget;
        let innerHTML;

        switch (codePreviewFor) {
          case "list":
            innerHTML = isNewWidget
              ? this.getNewListHTML(this.apiKey)
              : this.getListHTML(this.apiKey);
            break;
          case "teaser":
            innerHTML = isNewWidget
              ? this.getNewTeaserHTML(this.apiKey)
              : this.getTeaserHTML(this.apiKey);
            break;
          case "detail":
            innerHTML = isNewWidget
              ? this.getNewDetailHTML(this.apiKey)
              : this.getDetailHTML(this.apiKey);
            break;
          case "faketable":
            innerHTML = this.getNewFakeTableHTML(this.apiKey);
            break;
          case "accordion":
            innerHTML = this.getNewAccordionHTML(this.apiKey);
            break;
          case "reportWidget":
            innerHTML = this.getReportWidgetHTML(this.apiKey);
            break;
          case "iframe-list":
            innerHTML = this.getIframeHTML("events", this.apiKey.newWidget);
            this.refreshIframePreview.list++;
            break;
          case "iframe-teaser":
            innerHTML = this.getIframeHTML("teaser", this.apiKey.newWidget);
            this.refreshIframePreview.teaser++;
            break;
          case "iframe-accordion":
            innerHTML = this.getIframeAccordionHTML();
            this.refreshIframePreview.accordion++;
            break;
          case "iframe-faketable":
            innerHTML = this.getIframeFaketableHTML();
            this.refreshIframePreview.faketable++;
            break;
          case "iframe-reportWidget":
            innerHTML = this.getIframeHTMLReportWidget();
            this.refreshIframePreview.report++;
            break;
          default:
            throw new Error("can not set code preview");
        }

        if (codePreviewElement) {
          codePreviewElement.remove();
        }
        codePreviewContainer.lastChild.innerHTML = this.getPreviewOuterHTML(
          innerHTML
        );
      });

      window.Prism.highlightAll();
    }, 1000),
    getSelectedPrincipalIds(principal) {
      return principal?._id ?? principal;
    },
    clearInput(text, filteredItems) {
      if (filteredItems?.length === 1) this[text] = "";
    },
    async addDropFile(e) {
      if (this.isReadOnly) {
        return;
      }
      if (typeof this.apiKey.fallbackPicture !== "object")
        this.apiKey.fallbackPicture = {};
      const file = e?.dataTransfer?.files ?? e ?? false;
      if (file) {
        file.dataUrl = await this.readFileUrl(file);
        file.title = file.name;
        const data = await uploadFile(
          file,
          undefined,
          "events",
          this.apiKey.config_ref,
          progressEvent => {
            this.$store.dispatch(
              "createSuccess",
              Math.round((progressEvent.loaded * 100) / progressEvent.total) +
                " % hochgeladen.."
            );
          }
        );
        if (data?.url) this.apiKey.fallbackPicture = data;
        await this.$emit("saveKey");
      } else this.apiKey.fallbackPicture = {};
    },
    async confirmRemoveFallbackImage() {
      if (this.isReadOnly) {
        return;
      }
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie das Fallback-Bild löschen möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        await this.removeFile();
      }
    },
    async removeFile() {
      //api-service removes reference. if no reference left the file will be deleted
      this.apiKey.fallbackPicture = {};
    },
    readFileUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ({ target }) => {
          if (target?.result) resolve(target.result);
          else reject("Unable to read File");
        };
        reader.readAsDataURL(file);
      });
    },
    addCustomFeatureGroup() {
      if (this.isReadOnly) {
        return;
      }
      if (!this.apiKey.customFeatureGroups?.length)
        this.apiKey.customFeatureGroups = [];

      this.apiKey.customFeatureGroups.push({
        label: "",
        feature_ids: []
      });
      this.$emit("refreshApiKey");
    },
    removeCustomFeatureGroup(item) {
      if (this.isReadOnly) {
        return;
      }
      this.apiKey.customFeatureGroups.remove(item);
      this.$emit("refreshApiKey");
    },
    moveCustomFeatureGroup(oldIndex, newIndex) {
      if (this.isReadOnly) {
        return;
      }
      const oldItem = this.apiKey.customFeatureGroups[oldIndex];

      this.apiKey.customFeatureGroups[
        oldIndex
      ] = this.apiKey.customFeatureGroups[newIndex];
      this.apiKey.customFeatureGroups[newIndex] = oldItem;

      this.$emit("refreshApiKey");
    },
    updateValue(value, key) {
      this.apiKey[key] = value;
      this.$emit("refreshApiKey");
    },
    updatePreSelectedCategories(val) {
      if (!val?.length) {
        this.updateValue(val, "preSelectedCategories");
        return;
      }
      if (val.length < this.apiKey.preSelectedCategories.length) {
        this.updateValue(val, "preSelectedCategories");
        return;
      }

      // Function is triggered when saving without a difference in val and apiKey.preSelectedCategories.
      // This results to wrong data.
      // Return function if Arrays contain the same IDs to prevent this.
      if (val.length === this.apiKey.preSelectedCategories.length) {
        if (val.every(_ => this.apiKey.preSelectedCategories.includes(_))) {
          return;
        }
      }

      const newestAddition = val[val.length - 1];
      if (this.categoryGroups.find(group => group._id === newestAddition)) {
        this.updateValue([newestAddition], "preSelectedCategories");
        return;
      }

      const activeCategoryGroup = this.categoryGroups.find(group =>
        this.apiKey.preSelectedCategories.includes(group._id)
      );
      if (
        !activeCategoryGroup?.categories.find(
          group => group._id === newestAddition
        )
      ) {
        this.updateValue([newestAddition], "preSelectedCategories");
        return;
      }
      this.updateValue(val, "preSelectedCategories");
    },
    async reloadPrincipals() {
      await this.$store.dispatch("getCategoryGroupsForEvents", {
        additionalPrincipals: this.apiKey.selectedPrincipals
      });
      await this.$store.dispatch("getFeatureGroupsForEvents", {
        additionalPrincipals: this.apiKey.selectedPrincipals
      });
    },
    getUserName(id) {
      const user = this.allUsers.find(user => user._id.toString() === id);
      if (!user) {
        return "---";
      }
      return `${user.first_name} ${user.last_name}`;
    }
  },
  components: {
    InfoDialog,
    ImagePreview,
    TreeViewSelection,
    FallbackPictureCopyrightEditView,
    ColorPickerButton,
    LogOverview,
    VuePrism
  }
};
